/**
 * AadhaarVerificationModal — Modal for uploading Aadhaar verification documents.
 * Shows current verification status and allows submitting/updating documents.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, ShieldCheck, CheckCircle2, Upload, Sparkles, 
  AlertTriangle, ImagePlus, Trash2, Clock 
} from 'lucide-react';
import { toast } from 'sonner';
import { verificationApi } from '@/services/verificationApi';
import type { VerificationRecord } from '@/types/verification';

interface Props {
  userId: string;
  open: boolean;
  onClose: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

const AadhaarVerificationModal: React.FC<Props> = ({ userId, open, onClose }) => {
  const [step, setStep] = useState<'upload' | 'success'>('upload');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<VerificationRecord | null>(null);

  // Form state
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);

  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const frontPreviewRef = useRef<string | null>(null);
  const backPreviewRef = useRef<string | null>(null);

  // Keep refs updated with current preview URLs
  useEffect(() => {
    frontPreviewRef.current = frontPreview;
  }, [frontPreview]);

  useEffect(() => {
    backPreviewRef.current = backPreview;
  }, [backPreview]);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await verificationApi.getStatus(userId);
      if (data && 'id' in data) {
        setRecord(data as VerificationRecord);
        if ((data as VerificationRecord).status === 'PENDING') {
          // Keep it in upload step or show status
          setStep('upload');
        }
      } else {
        setRecord(null);
      }
    } catch (err) {
      // No record yet — that's fine
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) {
      fetchStatus();
      setStep('upload');
      setAadhaarNumber('');
      setFrontImage(null);
      setBackImage(null);
      if (frontPreviewRef.current) URL.revokeObjectURL(frontPreviewRef.current);
      if (backPreviewRef.current) URL.revokeObjectURL(backPreviewRef.current);
      setFrontPreview(null);
      setBackPreview(null);
      setError(null);
      setSubmitting(false);
    }
  }, [open, fetchStatus]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      if (frontPreviewRef.current) URL.revokeObjectURL(frontPreviewRef.current);
      if (backPreviewRef.current) URL.revokeObjectURL(backPreviewRef.current);
    };
  }, []);

  if (!open) return null;

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Only JPEG and PNG images are allowed.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" exceeds the 10MB size limit.`;
    }
    return null;
  };

  const handleFileSelect = (side: 'front' | 'back', file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    const previewUrl = URL.createObjectURL(file);
    if (side === 'front') {
      if (frontPreviewRef.current) URL.revokeObjectURL(frontPreviewRef.current);
      setFrontImage(file);
      setFrontPreview(previewUrl);
    } else {
      if (backPreviewRef.current) URL.revokeObjectURL(backPreviewRef.current);
      setBackImage(file);
      setBackPreview(previewUrl);
    }
  };

  const handleRemoveFile = (side: 'front' | 'back') => {
    if (side === 'front') {
      if (frontPreviewRef.current) URL.revokeObjectURL(frontPreviewRef.current);
      setFrontImage(null);
      setFrontPreview(null);
      if (frontInputRef.current) frontInputRef.current.value = '';
    } else {
      if (backPreviewRef.current) URL.revokeObjectURL(backPreviewRef.current);
      setBackImage(null);
      setBackPreview(null);
      if (backInputRef.current) backInputRef.current.value = '';
    }
  };

  const handleAadhaarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    let formattedValue = '';
    for (let i = 0; i < value.length; i++) {
      if (i > 0 && i % 4 === 0) {
        formattedValue += '-';
      }
      formattedValue += value[i];
    }
    setAadhaarNumber(formattedValue.substring(0, 14));
  };

  const handleSubmit = async () => {
    if (!frontImage || !backImage) {
      setError('Please upload both front and back photos of your Aadhaar card.');
      return;
    }
    
    const plainAadhaar = aadhaarNumber.replace(/-/g, '');
    if (!plainAadhaar || plainAadhaar.length < 12) {
      setError('Please enter a valid 12-digit Aadhaar number.');
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      const response = await verificationApi.uploadAadhaar(plainAadhaar, frontImage, backImage);
      toast.success(response.message || 'Aadhaar document submitted for verification!');
      setStep('success');
      await fetchStatus();
    } catch (err: any) {
      setError(err?.message || 'Failed to submit document. Please try again.');
      toast.error(err?.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const statusBadge = () => {
    if (!record) return null;
    switch (record.status) {
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="h-4 w-4" />
            Verified
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold bg-red-50 text-red-700 border border-red-200">
            <X className="h-4 w-4" />
            Rejected
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="h-4 w-4" />
            Pending Review
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-rose-100 bg-white p-6 sm:p-8 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-rose-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-md shadow-emerald-500/20">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-serif text-lg font-bold text-slate-900">Aadhaar Document Verification</h3>
              <p className="text-xs text-slate-500">Upload your Aadhaar card for identity verification</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 hover:bg-rose-50 hover:text-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-3 flex items-center gap-2 animate-in fade-in duration-150">
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            <p className="text-xs font-semibold text-red-700">{error}</p>
          </div>
        )}

        {/* Content Flow */}
        <div className="py-5">
          {/* Status view for existing records (PENDING, APPROVED) - only if we are in upload step, success step overrides this */}
          {step === 'upload' && !loading && record && record.status !== 'REJECTED' && (
            <div className="mb-6 rounded-2xl border border-rose-100 bg-slate-50 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Current Status</p>
                {statusBadge()}
              </div>
              
              {record.status === 'PENDING' && (
                <div className="flex items-center gap-3 mt-4 bg-amber-50/70 border border-amber-200 rounded-xl p-3">
                  <Clock className="h-5 w-5 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-900 font-medium">
                    Your documents are under review. This usually takes up to 24 hours.
                  </p>
                </div>
              )}
              
              {record.status === 'APPROVED' && (
                <div className="flex items-center gap-3 mt-4 bg-emerald-50/70 border border-emerald-200 rounded-xl p-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  <p className="text-xs text-emerald-900 font-medium">
                    Your Aadhaar identity has been successfully verified!
                  </p>
                </div>
              )}

              {record.adminNotes && (
                <p className="text-xs text-slate-600 mt-3 p-3 bg-white rounded-xl border border-slate-100">
                  <span className="font-bold text-slate-800">Admin Note:</span> {record.adminNotes}
                </p>
              )}
            </div>
          )}

          {/* Upload Step */}
          {step === 'upload' && !loading && (!record || record.status === 'REJECTED') && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {record?.status === 'REJECTED' && (
                <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-red-900">Previous submission rejected</p>
                    <p className="text-xs text-red-700 mt-1">
                      {record.adminNotes || 'Please upload clear images and try again.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Info Banner */}
              {!record && (
                <div className="rounded-2xl bg-amber-50/70 border border-amber-200 p-4 flex items-center gap-3">
                  <Upload className="h-5 w-5 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-900 font-medium">
                    Enter your Aadhaar number and upload clear photos (front & back). Documents will be verified manually within 24 hours.
                  </p>
                </div>
              )}

              {/* Aadhaar Number */}
              <div>
                <label htmlFor="aadhaarNumber" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Aadhaar Number
                </label>
                <div className="relative">
                  <input
                    id="aadhaarNumber"
                    type="text"
                    value={aadhaarNumber}
                    onChange={handleAadhaarChange}
                    placeholder="XXXX-XXXX-XXXX"
                    maxLength={14}
                    className="w-full rounded-2xl border-2 border-rose-100 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-900 placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:outline-none focus:ring-0 transition-colors"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                     <ShieldCheck className={`h-5 w-5 ${aadhaarNumber.length === 14 ? 'text-emerald-500' : 'text-slate-300'}`} />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5 font-medium ml-1">Only the last 4 digits will be visible to our admin team</p>
              </div>

              {/* Front Photo Upload */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Front Side of Aadhaar Card
                </label>
                {frontPreview ? (
                  <div className="relative rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-3 flex items-center gap-3 animate-in fade-in duration-150">
                    <img
                      src={frontPreview}
                      alt="Aadhaar front"
                      className="h-16 w-24 rounded-xl object-cover border border-slate-200"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-900 truncate">{frontImage?.name}</p>
                      <p className="text-[10px] text-slate-500">{frontImage ? formatFileSize(frontImage.size) : ''}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        <span className="text-[10px] font-semibold text-emerald-700">Ready</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('front')}
                      className="rounded-full p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="group flex cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed border-rose-300 bg-rose-50/40 p-6 transition-colors hover:border-crimson-400 hover:bg-rose-50/80">
                    <div className="flex flex-col items-center gap-1.5">
                      <ImagePlus className="h-7 w-7 text-crimson-500 group-hover:scale-110 transition-transform" />
                      <span className="text-xs font-bold text-slate-700">Click to upload front photo</span>
                      <span className="text-[10px] text-slate-400 font-medium">JPEG or PNG, up to 10MB</span>
                    </div>
                    <input
                      ref={frontInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelect('front', file);
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Back Photo Upload */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Back Side of Aadhaar Card
                </label>
                {backPreview ? (
                  <div className="relative rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-3 flex items-center gap-3 animate-in fade-in duration-150">
                    <img
                      src={backPreview}
                      alt="Aadhaar back"
                      className="h-16 w-24 rounded-xl object-cover border border-slate-200"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-900 truncate">{backImage?.name}</p>
                      <p className="text-[10px] text-slate-500">{backImage ? formatFileSize(backImage.size) : ''}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        <span className="text-[10px] font-semibold text-emerald-700">Ready</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('back')}
                      className="rounded-full p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="group flex cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed border-rose-300 bg-rose-50/40 p-6 transition-colors hover:border-crimson-400 hover:bg-rose-50/80">
                    <div className="flex flex-col items-center gap-1.5">
                      <ImagePlus className="h-7 w-7 text-crimson-500 group-hover:scale-110 transition-transform" />
                      <span className="text-xs font-bold text-slate-700">Click to upload back photo</span>
                      <span className="text-[10px] text-slate-400 font-medium">JPEG or PNG, up to 10MB</span>
                    </div>
                    <input
                      ref={backInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelect('back', file);
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !frontImage || !backImage || aadhaarNumber.length < 14}
                  className="w-full flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg disabled:opacity-50 transition-all hover:brightness-110"
                >
                  <Upload className="h-4 w-4" />
                  <span>{submitting ? 'Uploading & Submitting...' : 'Submit for Verification'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Success View */}
          {step === 'success' && (
            <div className="space-y-4 text-center py-4 animate-in zoom-in-95 duration-200">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-inner">
                <Sparkles className="h-8 w-8 animate-spin-slow" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-bold text-slate-900">
                  Document Submitted
                </h3>
                <p className="text-xs text-slate-600 mt-2 font-medium leading-relaxed max-w-sm mx-auto">
                  Your Aadhaar document has been submitted. Our admin team will verify it within 24 hours. You'll see a verified badge once approved.
                </p>
              </div>

              <div className="pt-4">
                <button
                  onClick={onClose}
                  className="w-full rounded-2xl bg-emerald-600 py-3 text-xs font-bold text-white shadow-md hover:bg-emerald-700 transition-colors"
                >
                  Done & Return to Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AadhaarVerificationModal;
